import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Collapsible from '@radix-ui/react-collapsible';
import * as Select from '@radix-ui/react-select';
import type { CitationResponse, SourceResponse } from 'we-tree-api-client/v2';
import CollapsibleChevron from '@/components/shared/CollapsibleChevron';
import { canModifyItem } from '@/types/access';
import CitationMediaThumb from '@/components/sources/CitationMediaThumb';
import Close from '@/icons/Close';
import Image from '@/icons/Image';
import MessageSquare from '@/icons/MessageSquare';
import Pencil from '@/icons/Pencil';
import Plus from '@/icons/Plus';
import { CONFIDENCE_OPTIONS, type ConfidenceOption } from '@/components/sources/confidenceOptions';
import {
  SOURCE_TYPE_LABELS,
  SOURCE_TYPE_META,
  SOURCE_TYPES,
  normalizeSourceType,
  type SourceType
} from '@/components/sources/sourceTypeMeta';

export interface SourceCardProps {
  familyId: string;
  source: SourceResponse;
  canEdit: boolean;
  isExpanded: boolean;
  onExpandedChange: (open: boolean) => void;
  citations: CitationResponse[] | undefined;
  citationsLoading: boolean;
  isEditingSource: boolean;
  editTitle: string;
  editType: SourceType;
  editUrl: string;
  submittingEditSource: boolean;
  onEditTitleChange: (value: string) => void;
  onEditTypeChange: (value: SourceType) => void;
  onEditUrlChange: (value: string) => void;
  onStartEditSource: () => void;
  onCancelEditSource: () => void;
  onSaveEditSource: (e: React.FormEvent) => void;
  onDeleteSource: () => void;
  showAddCitationForm: boolean;
  onToggleAddCitation: () => void;
  citationNotes: string;
  citationDetailLocator: string;
  citationConfidence: ConfidenceOption | '';
  submittingCitation: boolean;
  onCitationNotesChange: (value: string) => void;
  onCitationDetailLocatorChange: (value: string) => void;
  onCitationConfidenceChange: (value: ConfidenceOption | '') => void;
  onAddCitation: (e: React.FormEvent) => void;
  onChooseMediaForAdd: () => void;
  mediaAssetLabel: string | null;
  editingCitationId: string | null;
  editCitationDetailLocator: string;
  editCitationNotes: string;
  editCitationConfidence: ConfidenceOption | '';
  submittingEditCitation: boolean;
  onEditCitationDetailLocatorChange: (value: string) => void;
  onEditCitationNotesChange: (value: string) => void;
  onEditCitationConfidenceChange: (value: ConfidenceOption | '') => void;
  onStartEditCitation: (citation: CitationResponse) => void;
  onCancelEditCitation: () => void;
  onSaveEditCitation: (e: React.FormEvent) => void;
  onChooseMediaForEdit: () => void;
  editMediaAssetLabel: string | null;
  onViewCitationImage: (mediaAssetId: string) => void;
}

const SourceCard: React.FC<SourceCardProps> = ({
  familyId,
  source,
  canEdit,
  isExpanded,
  onExpandedChange,
  citations,
  citationsLoading,
  isEditingSource,
  editTitle,
  editType,
  editUrl,
  submittingEditSource,
  onEditTitleChange,
  onEditTypeChange,
  onEditUrlChange,
  onStartEditSource,
  onCancelEditSource,
  onSaveEditSource,
  onDeleteSource,
  showAddCitationForm,
  onToggleAddCitation,
  citationNotes,
  citationDetailLocator,
  citationConfidence,
  submittingCitation,
  onCitationNotesChange,
  onCitationDetailLocatorChange,
  onCitationConfidenceChange,
  onAddCitation,
  onChooseMediaForAdd,
  mediaAssetLabel,
  editingCitationId,
  editCitationDetailLocator,
  editCitationNotes,
  editCitationConfidence,
  submittingEditCitation,
  onEditCitationDetailLocatorChange,
  onEditCitationNotesChange,
  onEditCitationConfidenceChange,
  onStartEditCitation,
  onCancelEditCitation,
  onSaveEditCitation,
  onChooseMediaForEdit,
  editMediaAssetLabel,
  onViewCitationImage
}) => {
  const { t } = useTranslation('forms');
  const { t: tCommon } = useTranslation('common');
  const sourceType = normalizeSourceType(source.type);
  const { Icon: TypeIcon, label: typeLabel, tone: typeTone } = SOURCE_TYPE_META[sourceType];
  const citationsKnown = citations !== undefined;
  const citationCount = citations?.length ?? 0;

  const renderCitationFormFields = (
    idPrefix: string,
    detail: string,
    notes: string,
    confidence: ConfidenceOption | '',
    onDetailChange: (v: string) => void,
    onNotesChange: (v: string) => void,
    onConfidenceChange: (v: ConfidenceOption | '') => void,
    onChooseMedia: () => void,
    mediaLabel: string | null
  ) => (
    <>
      <div className="sources-form__row">
        <label className="sources-form__label" htmlFor={`${idPrefix}-detail`}>
          {t('sources.detailLocatorLabel')}
        </label>
        <input
          id={`${idPrefix}-detail`}
          type="text"
          className="sources-form__input"
          value={detail}
          onChange={(e) => onDetailChange(e.target.value)}
          placeholder={t('sources.detailLocatorPlaceholder')}
        />
      </div>
      <div className="sources-form__row">
        <label className="sources-form__label" htmlFor={`${idPrefix}-notes`}>
          {t('person.notes')}
        </label>
        <textarea
          id={`${idPrefix}-notes`}
          className="sources-form__textarea"
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          placeholder={t('sources.notesPlaceholder')}
        />
      </div>
      <div className="sources-form__row">
        <label className="sources-form__label" htmlFor={`${idPrefix}-confidence`}>
          {t('citations.confidence')}
        </label>
        <Select.Root
          value={confidence || '__none__'}
          onValueChange={(v) => onConfidenceChange(v === '__none__' ? '' : (v as ConfidenceOption))}
        >
          <Select.Trigger
            id={`${idPrefix}-confidence`}
            className="form-select-trigger"
            aria-label={t('citations.confidence')}
          >
            <Select.Value placeholder={tCommon('emptyDefault')} />
          </Select.Trigger>
          <Select.Portal>
            <Select.Content className="form-select-content" position="popper" sideOffset={4}>
              <Select.Item value="__none__" className="form-select-item">
                <Select.ItemText>—</Select.ItemText>
              </Select.Item>
              {CONFIDENCE_OPTIONS.map((c) => (
                <Select.Item key={c} value={c} className="form-select-item">
                  <Select.ItemText>{c}</Select.ItemText>
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Portal>
        </Select.Root>
      </div>
      <div className="sources-form__row">
        <label className="sources-form__label">{t('sources.linkToMediaOptional')}</label>
        <div className="sources-citation__media-row">
          <button type="button" className="btn btn--ghost btn--sm" onClick={onChooseMedia}>
            {t('sources.chooseImage')}
          </button>
          {mediaLabel && <span className="sources-citation__media-label">{mediaLabel}</span>}
        </div>
        <span className="sources-form__hint">{t('sources.chooseImageHint')}</span>
      </div>
    </>
  );

  return (
    <Collapsible.Root
      open={isExpanded}
      onOpenChange={onExpandedChange}
      className={`sources-card${showAddCitationForm ? ' sources-card--add-citation' : ''}`}
    >
      <div className="sources-card__header">
        <Collapsible.Trigger asChild>
          <button type="button" className="sources-card__trigger">
            <CollapsibleChevron className="sources-card__chevron" />
            <span className={`sources-card__type-icon sources-card__type-icon--${typeTone}`} aria-hidden>
              <TypeIcon width={16} height={16} />
            </span>
            <span className="sources-card__heading">
              <span className="sources-card__title">{source.title}</span>
              <span className="sources-card__meta-line">
                <span className={`sources-card__type-pill sources-card__type-pill--${typeTone}`}>{typeLabel}</span>
                {source.url && !isEditingSource && (
                  <>
                    <span className="sources-card__meta-sep" aria-hidden>
                      ·
                    </span>
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sources-card__url"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {source.url}
                    </a>
                  </>
                )}
              </span>
            </span>
          </button>
        </Collapsible.Trigger>

        <div className="sources-card__header-meta">
          <div className="sources-card__header-meta-row sources-card__header-meta-row--count">
            {!citationsKnown ? (
              <span className="sources-card__count-pill sources-card__count-pill--loading" aria-hidden>
                …
              </span>
            ) : (
              <span
                className="sources-card__count-pill"
                aria-label={t('sources.citationCount', { count: citationCount })}
              >
                {t('sources.citationCount', { count: citationCount })}
              </span>
            )}
          </div>
          {canEdit && !isEditingSource && (
            <div className="sources-card__header-meta-row sources-card__header-meta-row--actions">
              <div className="sources-card__actions profile-entry-actions">
                <button
                  type="button"
                  className="profile-entry-icon-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleAddCitation();
                  }}
                  title={showAddCitationForm ? tCommon('cancel') : t('sources.addCitation')}
                  aria-label={showAddCitationForm ? tCommon('cancel') : t('sources.addCitation')}
                >
                  {showAddCitationForm ? <Close width={15} height={15} /> : <Plus width={15} height={15} />}
                </button>
                <button
                  type="button"
                  className="profile-entry-icon-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onStartEditSource();
                  }}
                  title={t('sources.editSource')}
                  aria-label={t('sources.editSourceAria', { title: source.title })}
                >
                  <Pencil width={15} height={15} />
                </button>
                <button
                  type="button"
                  className="profile-entry-icon-btn profile-entry-icon-btn--danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteSource();
                  }}
                  title={t('sources.deleteSource')}
                  aria-label={t('sources.deleteSourceAria', { title: source.title })}
                >
                  <Close width={15} height={15} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {isEditingSource && (
        <form className="sources-form sources-form--card" onSubmit={onSaveEditSource}>
          <div className="sources-form__row">
            <label htmlFor={`edit-source-title-${source.id}`} className="sources-form__label">
              {t('sources.titleRequired')}
            </label>
            <input
              id={`edit-source-title-${source.id}`}
              type="text"
              className="sources-form__input"
              value={editTitle}
              onChange={(e) => onEditTitleChange(e.target.value)}
              required
            />
          </div>
          <div className="sources-form__row">
            <label htmlFor={`edit-source-type-${source.id}`} className="sources-form__label">
              {t('sources.type')}
            </label>
            <Select.Root value={editType} onValueChange={(v) => onEditTypeChange(v as SourceType)}>
              <Select.Trigger
                id={`edit-source-type-${source.id}`}
                className="form-select-trigger"
                aria-label={t('sources.sourceTypeAria')}
              >
                <Select.Value />
              </Select.Trigger>
              <Select.Portal>
                <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                  {SOURCE_TYPES.map((sourceTypeOption) => (
                    <Select.Item key={sourceTypeOption} value={sourceTypeOption} className="form-select-item">
                      <Select.ItemText>{SOURCE_TYPE_LABELS[sourceTypeOption]}</Select.ItemText>
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Portal>
            </Select.Root>
          </div>
          <div className="sources-form__row">
            <label htmlFor={`edit-source-url-${source.id}`} className="sources-form__label">
              {t('sources.urlOptional')}
            </label>
            <input
              id={`edit-source-url-${source.id}`}
              type="url"
              className="sources-form__input"
              value={editUrl}
              onChange={(e) => onEditUrlChange(e.target.value)}
            />
          </div>
          <div className="sources-form__actions">
            <button
              type="submit"
              className="btn btn--primary btn--sm"
              disabled={submittingEditSource || !editTitle.trim()}
            >
              {submittingEditSource ? tCommon('saving') : t('actions.save')}
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={onCancelEditSource}>
              {tCommon('cancel')}
            </button>
          </div>
        </form>
      )}

      <Collapsible.Content className="sources-card__content">
        <div className="sources-citations">
          {showAddCitationForm && (
            <form className="sources-form sources-form--inline" onSubmit={onAddCitation}>
              {renderCitationFormFields(
                `add-citation-${source.id}`,
                citationDetailLocator,
                citationNotes,
                citationConfidence,
                onCitationDetailLocatorChange,
                onCitationNotesChange,
                onCitationConfidenceChange,
                onChooseMediaForAdd,
                mediaAssetLabel
              )}
              <div className="sources-form__actions">
                <button type="submit" className="btn btn--primary btn--sm" disabled={submittingCitation}>
                  {submittingCitation ? t('actions.adding') : t('sources.addCitation')}
                </button>
                <button type="button" className="btn btn--ghost btn--sm" onClick={onToggleAddCitation}>
                  {tCommon('cancel')}
                </button>
              </div>
            </form>
          )}

          {citationsLoading ? (
            <p className="sources-citations__empty">{t('evidence.loadingCitations')}</p>
          ) : citationCount === 0 ? (
            <p className="sources-citations__empty">{t('sources.noCitations')}</p>
          ) : (
            <ul className="sources-citations__list">
              {citations?.map((citation) => (
                <li key={citation.id} className="sources-citation">
                  {editingCitationId === citation.id ? (
                    <form className="sources-form sources-form--inline" onSubmit={onSaveEditCitation}>
                      {renderCitationFormFields(
                        `edit-citation-${citation.id}`,
                        editCitationDetailLocator,
                        editCitationNotes,
                        editCitationConfidence,
                        onEditCitationDetailLocatorChange,
                        onEditCitationNotesChange,
                        onEditCitationConfidenceChange,
                        onChooseMediaForEdit,
                        editMediaAssetLabel
                      )}
                      <div className="sources-form__actions">
                        <button type="submit" className="btn btn--primary btn--sm" disabled={submittingEditCitation}>
                          {submittingEditCitation ? tCommon('saving') : t('actions.save')}
                        </button>
                        <button type="button" className="btn btn--ghost btn--sm" onClick={onCancelEditCitation}>
                          {tCommon('cancel')}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <div className="sources-citation__body">
                        {citation.mediaAssetId ? (
                          <CitationMediaThumb
                            familyId={familyId}
                            mediaAssetId={citation.mediaAssetId}
                            onClick={() => onViewCitationImage(citation.mediaAssetId!)}
                          />
                        ) : (
                          <MessageSquare width={18} height={18} className="sources-citation__icon" />
                        )}
                        <p className="sources-citation__text">
                          {citation.detailLocator && (
                            <span className="sources-citation__detail">{citation.detailLocator}</span>
                          )}
                          {citation.detailLocator && citation.notes && ' — '}
                          {citation.notes}
                        </p>
                      </div>
                      <div className="sources-citation__trailing">
                        {citation.confidence && (
                          <span className="sources-citation__confidence" title={t('sources.confidenceTitle')}>
                            {citation.confidence}
                          </span>
                        )}
                        <div className="sources-citation__actions profile-entry-actions">
                          {canModifyItem(citation, canEdit) && (
                            <button
                              type="button"
                              className="profile-entry-icon-btn"
                              onClick={() => onStartEditCitation(citation)}
                              title={t('sources.editCitation')}
                              aria-label={t('sources.editCitation')}
                            >
                              <Pencil width={15} height={15} />
                            </button>
                          )}
                          <span
                            className="profile-entry-icon-btn-wrap"
                            title={citation.mediaAssetId ? undefined : t('sources.noImageAttached')}
                          >
                            <button
                              type="button"
                              className="profile-entry-icon-btn"
                              disabled={!citation.mediaAssetId}
                              onClick={() => {
                                if (citation.mediaAssetId) {
                                  onViewCitationImage(citation.mediaAssetId);
                                }
                              }}
                              title={citation.mediaAssetId ? t('citations.viewImage') : undefined}
                              aria-label={
                                citation.mediaAssetId
                                  ? t('sources.viewCitationImage')
                                  : t('sources.viewCitationImageNoImage')
                              }
                            >
                              <Image width={15} height={15} />
                            </button>
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Collapsible.Content>
    </Collapsible.Root>
  );
};

export default SourceCard;
